import * as React from 'react';
import * as yup from 'yup';

import { Grid, Typography, TextField, Box, Button, FormControlLabel, Checkbox, Autocomplete } from '@mui/material';

import { FormContext } from '../../context/FormContext';
import { OrderContext } from '../../context/OrderContext';
import { useSubscriber } from '../../context/SubscribersContext';
import { ManagerContext } from '../../context/ManagerContext';
import { useEvent } from '../../context/EventContext';
import { api } from '../../services/api';
import { validateBr } from 'js-brasil';
import { maskCPF2, maskTEL, maskPassport, maskDate, dateToISO } from '../../utils/mask';
import isValidEmail from '../../utils/isValidEmail';
import { FieldArray, Form, Formik, getIn } from "formik";
import { subYears, format, parseISO} from 'date-fns';


export default function AddSubscriber() {
  const { activeStep, setActiveStep } = React.useContext(FormContext);
  const { order, setOrder } = React.useContext(OrderContext)
  const { events, setEvents } = useEvent();
  const { manager } = React.useContext(ManagerContext)
  const subscribers = []
  let load = false
  const { setSubscriber } = useSubscriber()
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 960);
  const [blocked, setBlocked] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState(null);

  function addFields() {
    for (let i = 1; i <= order.quantity; i++) {
      subscribers.push({ isPassport: false, document: "", name: "", badge: "", whatsapp: "", email: "", office: "", birth: "", gender: "", tshirtSize: "" });
    }
  }
  yup.addMethod(yup.array, 'unique', function (field, message) {
    return this.test('unique', message, function (array) {
      if (field === 'whatsapp' && isRelaxed) return true;
      const uniqueData = Array.from(
        new Set(array.map((row) => row[field]?.toLowerCase())),
      );
      const isUnique = array.length === uniqueData.length;
      if (isUnique) {
        return true;
      }
      const index = array.findIndex(
        (row, i) => row[field]?.toLowerCase() !== uniqueData[i],
      );
      console.log(index, array, array[index], field)
      if (array[index] === undefined) {
        return this.createError({
          path: `${this.path}.${index}.${field}`,
          message: `${this.path}.${index}.${field} precisa ser preenchido`,
        });
      }
      if (array[index][field] === '') {
        return true;
      }
      return this.createError({
        path: `${this.path}.${index}.${field}`,
        message
      });
    });
  });

  const isRelaxed = manager.config_path == 'agip' && String(order?.event ?? events?.id) === '71';

  const subscriberValidate = yup.object().shape({
    subscribers: yup.array().of(yup.object().shape({
      isPassport: yup.boolean(),
      document: yup.string()
        .test("document-validation", "Documento inválido", function (value) {
          const isPassport = this.parent.isPassport;
          if (isPassport) {
            return value && value.length >= 6; // Validação básica de passaporte
          }
          return validateBr.cpf(value || " ");
        })
        .required('CPF precisa ser preenchido'),
      name: yup.string()
        .min(8, 'Nome inválido (muito curto)')
        .required('Nome precisa ser preenchido'),
      badge: isRelaxed ? yup.string()
        .min(3, 'Nome para crachá inválido (muito curto)')
        .required('Nome para crachá precisa ser preenchido') : yup.string()
        .optional()
        .min(3, 'Nome para crachá inválido (muito curto)'),
      tshirtSize:  manager.config_path == 'agip' ? yup.string()
        .oneOf(['P', 'M', 'G', 'GG', 'EG', 'EGG'], 'Tamanho de camiseta inválido')
        .optional() : yup.string()
          .oneOf(['P', 'M', 'G', 'GG', 'EG', 'EGG'], 'Tamanho de camiseta inválido')
          .required('Tamanho de camiseta precisa ser preenchido'),
      email: yup.string()
        .test('email', 'E-mail inválido', value => !value || isValidEmail(value))
        .test('email-required', 'Email precisa ser preenchido', value => isRelaxed || !!value),
      birth: yup.string()
        .test('valid-date', 'Data inválida', function(value) {
          if (!value) return !!isRelaxed;
          
          // Se está em formato dd/mm/yyyy
          if (value.includes('/')) {
            if (value.length !== 10) return false;
            const [day, month, year] = value.split('/');
            const date = new Date(year, month - 1, day);
            return date.getDate() == day && 
                   date.getMonth() == month - 1 && 
                   date.getFullYear() == year;
          }
          
          // Se está em formato yyyy-mm-dd
          if (value.includes('-')) {
            const date = new Date(value + 'T00:00:00');
            return !isNaN(date.getTime());
          }
          
          return false;
        })
        .test('min-age', 'Menor de 18 anos', function(value) {
          if (!value) return !!isRelaxed;
          
          let date;
          if (value.includes('/')) {
            const [day, month, year] = value.split('/');
            date = new Date(year, month - 1, day);
          } else {
            date = new Date(value + 'T00:00:00');
          }
          
          const minDate = subYears(new Date(), 100);
          const maxDate = subYears(new Date(), 17);
          return date >= minDate && date <= maxDate;
        })
        .test('max-age', 'Maior de 100 anos', function(value) {
          if (!value) return !!isRelaxed;
          
          let date;
          if (value.includes('/')) {
            const [day, month, year] = value.split('/');
            date = new Date(year, month - 1, day);
          } else {
            date = new Date(value + 'T00:00:00');
          }
          
          const minDate = subYears(new Date(), 100);
          return date >= minDate;
        })
        .test('birth-required', 'Data de Nascimento precisa ser preenchida', value => isRelaxed || !!value),
      gender: manager.config_path == 'agip' ? yup.string()
        .oneOf(['M', 'F', 'Outro'], 'Sexo inválido')
        .optional() : yup.string()
          .oneOf(['M', 'F', 'Outro'], 'Sexo inválido')
          .required('Gênero precisa ser preenchido'),
      whatsapp: yup.string()
        .test('whatsapp-min', 'Celular inválido', value => !value || value.length >= 15)
        .test('whatsapp-required', 'Whatsapp precisa ser preenchido', value => isRelaxed || !!value),
      office: yup.string()
        .test('office-min', 'Minimo 4 letras', value => !value || value.length >= 4)
        .test('office-required', 'Cargo precisa ser preenchido', value => isRelaxed || !!value)
    }))
      .unique('document', 'CPF não pode ser repetido.')
      .unique('name', 'Nome não pode ser repetido.')
      .unique('whatsapp', 'Whatsapp não pode ser repetido.')
      .unique('badge', 'Nome para crachá não pode ser repetido.')

  })
  function maskDocument(e) {
    if (e.target.value.length > 14) { return }
    console.log(e.target.value, maskCPF2(e.target.value))
    if (e.target.value.length > 0) { return maskCPF2(e.target.value) }
    return e.target.value
  }
  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 960)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  React.useEffect(() => {
    if (!load) {
      if (order?.quantity !== order.subscribers?.length) {
        load = true
        return addFields()
      }
      if (order.subscribers?.length > 0) {
        if (subscribers.length < order.subscribers.length) {
          order.subscribers.map(sub => {
            subscribers.push({ document: sub.document, name: sub.name, badge: sub.badge, whatsapp: sub.whatsapp, gender: sub.gender, birth: sub.birth, email: sub.email, office: sub.office, tshirtSize: sub.tshirtSize, isPassport: sub.isPassport })
          })
          load = true
        }
      } else {
        if (subscribers.length == 0) {
          load = true
          return addFields()
        }
      }
    }
  }, [])


  return (
    <React.Fragment>
      <Typography variant="h6" gutterBottom>
        Adicionar Inscritos
      </Typography>
      {errorMessage && (
        <Typography color="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Typography>
      )}
      <Formik
        initialValues={{ subscribers }}
        validationSchema={subscriberValidate}
        validateOnChange={true}
        validateOnMount={false}
        enableReinitialize
        onSubmit={values => {
          // Converte as datas para formato ISO antes de enviar
          const subscribersFormatted = values.subscribers.map(sub => {
            let birthFormatted = sub.birth;
            
            // Se a data está em formato dd/mm/yyyy, converte para yyyy-mm-dd
            if (sub.birth && sub.birth.includes('/')) {
              birthFormatted = dateToISO(sub.birth);
            }
            
            return {
              ...sub,
              birth: birthFormatted
            };
          });
          
          setSubscriber(subscribersFormatted)
          setOrder({ ...order, subscribers: subscribersFormatted })
          setActiveStep(activeStep + 1)
        }}
      >
        {({ values, touched, errors, handleChange, handleBlur, isValid, setFieldValue, setFieldError }) => (
          <Form autoComplete="off">
            <FieldArray name="subscribers">
              {({ push, remove }) =>
              (
                <div>
                  {values?.subscribers?.map((p, index) => {
                    const document = `subscribers[${index}].document`;
                    const toucheddocument = getIn(touched, document);
                    const errordocument = getIn(errors, document);

                    const badge = `subscribers[${index}].badge`;
                    const touchedbadge = getIn(touched, badge);
                    const errorbadge = getIn(errors, badge);

                    const name = `subscribers[${index}].name`;
                    const touchedname = getIn(touched, name);
                    const errorname = getIn(errors, name);

                    const email = `subscribers[${index}].email`;
                    const touchedemail = getIn(touched, email);
                    const erroremail = getIn(errors, email);

                    const birth = `subscribers[${index}].birth`;
                    const touchedbirth = getIn(touched, birth);
                    const errorbirth = getIn(errors, birth);

                    const tshirtSize = `subscribers[${index}].tshirtSize`;
                    const touchedtshirtSize = getIn(touched, tshirtSize);
                    const errortshirtSize = getIn(errors, tshirtSize);

                    const gender = `subscribers[${index}].gender`;
                    const touchedgender = getIn(touched, gender);
                    const errorgender = getIn(errors, gender);

                    const office = `subscribers[${index}].office`;
                    const touchedoffice = getIn(touched, office);
                    const erroroffice = getIn(errors, office);

                    const whatsapp = `subscribers[${index}].whatsapp`;
                    const touchedwhatsapp = getIn(touched, whatsapp);
                    const errorwhatsapp = getIn(errors, whatsapp);

                    return (
                      <React.Fragment key={index + 1}>
                        <Typography variant="subtitle2">Inscrito N. {index + 1}</Typography>
                        <Grid container columns={12} spacing={1} key={p.id}>
                          <Grid item xs={12}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={p.isPassport}
                                    onChange={(e) => {
                                      setFieldValue(`subscribers[${index}].isPassport`, e.target.checked);
                                      setFieldValue(`subscribers[${index}].document`, ''); // Limpa o campo ao trocar
                                    }}
                                    disabled={blocked}
                                />
                              }
                              label="Estrangeiro (usar passaporte)"
                            />
                          </Grid>
                          <Grid item xs={12} md={1.5}>
                            <TextField
                              id={document}
                              name={document}
                              label={p.isPassport ? "Passaporte" : "CPF"}
                              fullWidth
                              variant="outlined"
                              disabled={blocked}
                              inputProps={{
                                maxLength: p.isPassport ? 20 : 14,
                                style: p.isPassport ? { textTransform: 'uppercase' } : {}
                              }}
                              value={p.document}
                              onChange={async (e) => {
                                let value = p.isPassport
                                  ? maskPassport(e.target.value)
                                  : maskDocument(e);

                                setFieldValue(document, value);

                                if (!p.isPassport && validateBr.cpf(value)) {
                                  try {
                                    const { data } = await api.post(`/subscriber/form`, { data: { cpf: value } });
                                    // checa estrutura de erro retornada: { data: null, error: { status, message } }
                                    if (data && data?.name?.length > 0) {
                                      const subscriber = data;
                                      setFieldValue(name, subscriber.name || "");
                                      setFieldValue(badge, subscriber.badge || "");
                                      setFieldValue(email, subscriber.email || "");
                                      setFieldValue(birth, subscriber.birth || "");
                                      setFieldValue(gender, subscriber.gender || "");
                                      setFieldValue(whatsapp, subscriber.whatsapp || "");
                                      setFieldValue(office, subscriber.office || "");
                                      setFieldValue(`subscribers[${index}].tshirtSize`, subscriber.tshirtSize || "");
                                    }
                                  } catch (error) {
                                    console.log(error)
                                    if (error.response && error.response.status === 403) {
                                      alert(error.response?.data?.error?.message || 'ENTRAR EM CONTATO COM A APEPREV - 41 98791-4672');
                                      const msg = error.response?.data?.error?.message || 'ENTRAR EM CONTATO COM A APEPREV - 41 98791-4672';
                                      setBlocked(true);
                                      setErrorMessage(msg);
                                      return;
                                    }
                                    console.error("Erro ao buscar dados do inscrito:", error);
                                  }
                                }
                              }}
                              helperText={toucheddocument && errordocument ? errordocument : ""}
                              error={toucheddocument && Boolean(errordocument)}
                            />
                          </Grid>
                          <Grid item xs={12} md={2}>
                            <TextField
                              id={name}
                              variant="outlined"
                              label="Nome Completo"
                              name={name}
                              fullWidth
                              disabled={blocked}
                              value={p.name}
                              helperText={
                                touchedname && errorname
                                  ? errorname
                                  : ""
                              }
                              error={Boolean(touchedname && errorname)}
                              onChange={handleChange}
                            />
                          </Grid>
                          <Grid item xs={12} md={1}>
                            <TextField
                              id={badge}
                              variant="outlined"
                              label="Nome p/ Cracha"
                              name={badge}
                              fullWidth
                              disabled={blocked}
                              value={p.badge}
                              helperText={
                                touchedbadge && errorbadge
                                  ? errorbadge
                                  : ""
                              }
                              error={Boolean(touchedbadge && errorbadge)}
                              onChange={handleChange}
                            />
                          </Grid>
                          <Grid item xs={12} md={2}>
                            <TextField
                              id={email}
                              label="Email"
                              name={email}
                              fullWidth
                              disabled={blocked}
                              value={p.email}
                              onChange={handleChange}
                              helperText={
                                touchedemail && erroremail
                                  ? erroremail
                                  : ""
                              }
                              error={Boolean(touchedemail && erroremail)}
                              variant="outlined"
                            />
                          </Grid>
                          <Grid item xs={12} md={1.5} sm={2} >
                            {isMobile ? (
                              <TextField
                                id={birth}
                                label="Data de Nascimento"
                                name={birth}
                                fullWidth
                                disabled={blocked}
                                type='text'
                                placeholder='DD/MM/AAAA'
                                value={(() => {
                                  // Se já está em formato dd/mm/yyyy, mantém
                                  if (p.birth && p.birth.includes('/')) {
                                    return p.birth
                                  }
                                  // Se está em formato ISO (yyyy-mm-dd), converte para dd/mm/yyyy
                                  if (p.birth && p.birth.includes('-') && p.birth.length === 10) {
                                    try {
                                      const date = new Date(p.birth + 'T00:00:00')
                                      if (!isNaN(date.getTime())) {
                                        return format(date, 'dd/MM/yyyy')
                                      }
                                    } catch (e) {
                                      return ''
                                    }
                                  }
                                  return p.birth || ''
                                })()}
                                onChange={(e) => {
                                  const maskedValue = maskDate(e.target.value)
                                  setFieldValue(birth, maskedValue)
                                }}
                                onBlur={(e) => {
                                  const value = e.target.value
                                  // Mantém no formato dd/mm/yyyy, não converte para ISO
                                  if (value.length === 10 && value.includes('/')) {
                                    // Apenas valida se a data é válida
                                    const [day, month, year] = value.split('/')
                                    const dateObj = new Date(year, month - 1, day)
                                    if (dateObj.getDate() == day && 
                                        dateObj.getMonth() == month - 1 && 
                                        dateObj.getFullYear() == year) {
                                      // Mantém no formato brasileiro
                                      setFieldValue(birth, value)
                                    }
                                  }
                                  handleBlur(e)
                                }}
                                helperText={
                                  touchedbirth && errorbirth
                                    ? errorbirth
                                    : "DD/MM/AAAA"
                                }
                                error={Boolean(touchedbirth && errorbirth)}
                                variant="outlined"
                                inputProps={{ maxLength: 10 }}
                              />
                            ) : (
                              <TextField
                                id={birth}
                                label="Data de Nascimento"
                                name={birth}
                                fullWidth
                                disabled={blocked}
                                type='date'
                                focused
                                value={p.birth && p.birth.includes('-') ? p.birth : ''}
                                onChange={handleChange}
                                helperText={
                                  touchedbirth && errorbirth
                                    ? errorbirth
                                    : ""
                                }
                                error={Boolean(touchedbirth && errorbirth)}
                                variant="outlined"
                              />
                            )}
                          </Grid>
                          <Grid item xs={12} md={1}>
                            <TextField
                              id={gender}
                              label="Gênero"
                              name={gender}
                              fullWidth
                              disabled={blocked}
                              type='text'
                              focused
                              value={p.gender}
                              onChange={handleChange}
                              helperText={
                                touchedgender && errorgender
                                  ? errorgender
                                  : "M, F ou Outro"
                              }
                              error={Boolean(touchedgender && errorgender)}
                              variant="outlined"
                            />
                          </Grid>
                          <Grid item xs={12} md={manager.config_path !== 'agip' ? 1 : 2}>
                            <TextField
                              id={whatsapp}
                              label="Whatsapp"
                              fullWidth
                              name={whatsapp}
                              value={p.whatsapp}
                              disabled={blocked}
                              onChange={(e) => {
                                let whats = maskTEL(e.target.value)
                                setFieldValue(whatsapp, whats)
                              }
                              }
                              helperText={
                                touchedwhatsapp && errorwhatsapp
                                  ? errorwhatsapp
                                  : ""
                              }
                              error={Boolean(touchedwhatsapp && errorwhatsapp)}
                              variant="outlined"
                            />
                          </Grid>
                          {manager.config_path !== 'agip' &&
                            <Grid item xs={12} md={1}>
                              <Autocomplete
                                onChange={(e) => {
                                  setFieldValue(tshirtSize, e.target.value.toUpperCase())
                                }}
                                inputValue={p.tshirtSize}
                                onInputChange={(event, newInputValue) => {
                                  setFieldValue(tshirtSize, newInputValue.toUpperCase())
                                }}
                                disablePortal
                                disabled={blocked}
                                id="tshirtSize"
                                name={tshirtSize}
                                value={p.tshirtSize}
                                defaultValue={p.tshirtSize}
                                options={['P', 'M', 'G', 'GG', 'EG', 'EGG']}
                                renderInput={(params) => <TextField
                                  {...params}
                                  label="Tamanho Camiseta"
                                  fullWidth
                                  helperText={
                                    touchedtshirtSize && errortshirtSize
                                      ? errortshirtSize
                                      : ""
                                  }
                                  error={Boolean(touchedtshirtSize && errortshirtSize)}
                                  variant="outlined"
                                />}
                              />

                            </Grid>
                          }
                          <Grid item xs={12} md={1}>
                            <TextField
                              id={office}
                              label="Cargo ou Função"
                              fullWidth
                              disabled={blocked}
                              name={office}
                              value={p.office}
                              onChange={handleChange}
                              helperText={
                                touchedoffice && erroroffice
                                  ? erroroffice
                                  : ""
                              }
                              error={Boolean(touchedoffice && erroroffice)}
                              variant="outlined"
                            />
                          </Grid>
                        </Grid>
                      </React.Fragment>
                    );
                  })}
                </div>
              )}
            </FieldArray>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                type={'button'}
                onClick={() => {
                  setSubscriber([]);
                  setActiveStep(activeStep - 1)
                }
                }
                sx={{ mt: 3, ml: 1 }}>
                Voltar
              </Button>
              <Button
                variant="contained"
                type='submit'
                sx={{ mt: 3, ml: 1 }}
                disabled={blocked}>
                Revisar Inscrição
              </Button>
            </Box>
          </Form>
        )}
      </Formik>

    </React.Fragment>
  );
}